import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import startCase from 'lodash.startcase';
import i18next from 'i18next';
import { useSelector } from 'react-redux';
import Status from '../RecentTransactions/Status';
import {
  getIsReadOnlyUser,
  getIsFullAccessUser,
  getIsPrimaryUser,
} from '../../../store/selectors/accountSelectors';
import { formatMoneyNumeral } from '../../../util/helpers/numeralHelpers';
import useWindowSize from '../../../util/hooks/useIsMobileHook';
import Tooltip from '../../Tooltip/Tooltip';
import { parseEnumType } from '../../../util/helpers/enumMappers';
import { transactionTypesArray } from '../../../util/enum/api/transactionTypes';
import Button from '../../Button/Button';

import { pendingTransactionCancel } from '../../Table/Components/PendingTransaction';
import { pxToRem } from '../../../assets/styles/helper';
import { variables } from '../../../assets/styles/variables';
import themeColors from '../../../assets/styles/themeColors';
import { uFlexColumn } from '../../../assets/styles/utility';
import useCheckPermissions from '../../../util/hooks/useCheckPermissions';
import { buttonizeDiv } from '../../../util/buttonizeDiv';

const TransactionTableTransaction = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  ${({ clickable }) => clickable && 'cursor: pointer;'}
`;

const TransactionTableDate = styled.p`
  font-size: ${pxToRem(16)};
  font-weight: 600;
  letter-spacing: 0;
  line-height: 1.5;
  color: ${themeColors.colorTextSecondary};
  max-width: ${pxToRem(38)};
  width: 100%;
  text-align: center;
  margin-right: ${pxToRem(32)};
  text-transform: uppercase;
`;

const TransactionTableActions = styled.div`
  ${uFlexColumn};
  justify-content: center;
  min-width: 0;
`;

const TransactionTableMinusSign = styled.span`
  margin-left: ${pxToRem(32)};
`;

const AmountCaption = styled.p`
  font-family: ${variables.family.goldSilver};
  font-weight: 400;
  font-size: ${pxToRem(16)};
  line-height: ${pxToRem(18)};
  letter-spacing: 0;
  color: ${themeColors.colorTextSecondary};
`;

const TriggerOrderLabelText = styled.p`
  font-family: ${variables.family.goldSilver};
  font-weight: 400;
  font-size: ${pxToRem(16)};
  line-height: ${pxToRem(18)};
  letter-spacing: 0;
  color: ${themeColors.colorTextSecondary};
`;

const TriggerOrderMediumText = styled.p`
  font-family: ${variables.family.goldSilver};
  font-weight: 500;
  font-size: ${pxToRem(16)};
  line-height: ${pxToRem(18)};
  letter-spacing: 0;
`;

const PendingTransactionRow = ({
  date,
  action,
  product,
  isSegregated,
  segregatedCaption,
  vaultName,
  label,
  amount,
  amountCaption,
  status,
  accountUid,
  paymentUid,
  canCancel,
  handleModalOpen,
  onRowClick,
  isTriggerOrder,
}) => {
  const windowSize = useWindowSize();
  const isMobile = windowSize.width < 576;
  const transactionTypeConst = parseEnumType(transactionTypesArray, action);
  const { allowAccountChangesPermission } = useCheckPermissions();
  const isReadOnlyUser = useSelector(getIsReadOnlyUser);
  const isFullAccessUser = useSelector(getIsFullAccessUser);
  const isPrimaryUser = useSelector(getIsPrimaryUser);
  const isIneligibleToCancel =
    isReadOnlyUser || (isFullAccessUser && !isPrimaryUser);
  const transactionType = amount
    ? i18next.t(
        `portfolio.transactions.portfolioTypes.${transactionTypeConst}`,
        {
          amount: formatMoneyNumeral(amount),
        },
      )
    : i18next.t(
        `portfolio.transactions.portfolioTypesNonAmount.${transactionTypeConst}`,
      );

  return (
    <>
      <TransactionTableTransaction
        clickable={!!onRowClick}
        {...(onRowClick ? buttonizeDiv(onRowClick) : {})}
      >
        {date && <TransactionTableDate>{date}</TransactionTableDate>}
        {(action || product || label) && (
          <TransactionTableActions>
            {label ? (
              <TriggerOrderLabelText>{label}</TriggerOrderLabelText>
            ) : (
              action && <p>{transactionType}</p>
            )}
            {product && (
              <TriggerOrderMediumText>{product}</TriggerOrderMediumText>
            )}
            {vaultName && (
              <TriggerOrderMediumText>
                {vaultName}
                {isSegregated && ` (${segregatedCaption})`}
              </TriggerOrderMediumText>
            )}
          </TransactionTableActions>
        )}
      </TransactionTableTransaction>
      {amount !== undefined && (
        <div>
          <TriggerOrderMediumText>
            {amount === null || amount === 0 ? (
              <TransactionTableMinusSign>&#8722;</TransactionTableMinusSign>
            ) : (
              formatMoneyNumeral(amount)
            )}
          </TriggerOrderMediumText>
          {amountCaption && <AmountCaption>{amountCaption}</AmountCaption>}
        </div>
      )}
      {isMobile ? (
        <Tooltip text={startCase(status)}>
          <Status status={status} />
        </Tooltip>
      ) : (
        <Status status={status} />
      )}
      <div onClick={(event) => event.stopPropagation()} role="presentation">
        <Button
          css={pendingTransactionCancel}
          onClick={() => handleModalOpen(accountUid, paymentUid, amount)}
          disabled={
            !canCancel ||
            !allowAccountChangesPermission ||
            (isTriggerOrder && isIneligibleToCancel)
          }
          variant="primaryOutlined"
          size="sm"
        >
          {i18next.t('common.cancel')}
        </Button>
      </div>
    </>
  );
};

PendingTransactionRow.propTypes = {
  date: PropTypes.string,
  amount: PropTypes.number,
  amountCaption: PropTypes.string,
  status: PropTypes.string,
  action: PropTypes.number,
  product: PropTypes.string,
  isSegregated: PropTypes.bool,
  segregatedCaption: PropTypes.string,
  vaultName: PropTypes.string,
  label: PropTypes.string,
  accountUid: PropTypes.string,
  paymentUid: PropTypes.string,
  handleModalOpen: PropTypes.func,
  canCancel: PropTypes.bool,
  onRowClick: PropTypes.func,
  isTriggerOrder: PropTypes.bool,
};

export default PendingTransactionRow;
